// Mono keyboard chip. Single source of truth for `⌘K`, `↵`, `→` affordances
// shown next to actions across the authed app. The app signals its
// opinions (and rewards keyboard users) by making shortcuts visible.
//
// Usage: <Kbd>⌘</Kbd><Kbd>K</Kbd>   or   <Kbd combo="⌘K" />

import type React from "react";
import { cn } from "../lib/cn";

interface Props {
  children?: React.ReactNode;
  /** Convenience for multi-key combos; splits visually with a thin gap. */
  combo?: string;
  className?: string;
}

export default function Kbd({ children, combo, className }: Props) {
  if (combo) {
    const keys = combo.split("").filter(Boolean);
    return (
      <span className={cn("inline-flex items-center gap-0.5", className)}>
        {keys.map((k, i) => (
          <KbdChip key={i}>{k}</KbdChip>
        ))}
      </span>
    );
  }
  return <KbdChip className={className}>{children}</KbdChip>;
}

function KbdChip({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <kbd
      className={cn(
        "inline-flex h-[18px] min-w-[18px] items-center justify-center rounded",
        "border border-border bg-bg-raised px-1",
        "font-mono text-[10px] font-medium text-fg-muted",
        "align-middle leading-none tabular-nums",
        className,
      )}
    >
      {children}
    </kbd>
  );
}
